// A saved Rx opens on the cells and applied rates stored with it. The field's
// current grid is only a comparison for the boundary notice. Spread files are
// clipped later, at export, and that clip is not stored on the Rx.
import { api, ApiError } from "../api";
import type { ManifestLayer } from "../types/contract";
import type { FertSummary } from "../stores/fertRecsStore";
import { assembleFertLayers, summarizeRecs, type RecInputs } from "./fertility";
import type { SavedRecSet } from "./fertSets";

export const CELLS_UNAVAILABLE =
  "Saved before cells were stored; its cells can't be recovered. Use Copy to new Rx.";

export const FILE_CLIPPED =
  "The spread file was clipped to the current field boundary.";

export const OPEN_FAILED = "This Rx couldn't be opened. Try again.";

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

export type FrozenNutrient = "lime" | "p2o5" | "k2o";

const FROZEN_NUTRIENTS: FrozenNutrient[] = ["lime", "p2o5", "k2o"];

type Geom = { type: string; coordinates: unknown };

export interface OpenedRec {
  set: SavedRecSet;
  cellGeometries: Geom[] | null;
}

export interface FrozenSpread {
  geoms: Geom[];
  notice: string | null;
  fileNotice: string | null;
}

export function isFrozen(set: { frozen?: { v?: unknown } | null }): boolean {
  return set.frozen?.v === 1;
}

export function cellsUnavailable(set: SavedRecSet): boolean {
  return isFrozen(set) && set.frozen?.cells == null;
}

/** The boundary version stored on a grid-backed Rx. Snapshots have none. */
export function savedBoundaryId(set: SavedRecSet): string | undefined {
  const cells = set.frozen?.cells;
  if (cells && cells.kind === "grid" && cells.gridId) return cells.gridId;
  return undefined;
}

function boundaryDate(at: string | undefined): string | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(at ?? "");
  if (!match) return null;
  const month = MONTHS[Number(match[2]) - 1];
  if (!month) return null;
  return `${month} ${Number(match[3])}, ${match[1]}`;
}

/**
 * The older-boundary notice compares boundary versions. It does not read a
 * spread file's `clipped` flag: a real grid keeps every cell that touches
 * the boundary, so that flag is true even when the boundary never changed.
 */
export function olderBoundaryNotice(
  savedId: string | undefined,
  currentId: string | undefined,
  at?: string,
): string | null {
  if (!savedId || !currentId || savedId === currentId) return null;
  const date = boundaryDate(at);
  return date
    ? `Built on the boundary of ${date}`
    : "Built on an older boundary";
}

export function fileClipNotice(
  savedId: string | undefined,
  currentId: string | undefined,
): string | null {
  if (!savedId || !currentId || savedId === currentId) return null;
  return FILE_CLIPPED;
}

export async function openRecDocument(
  fieldId: string,
  set: SavedRecSet,
): Promise<OpenedRec> {
  if (!set.id) return { set, cellGeometries: null };
  try {
    const fresh = (await api.fetchFertRxById(
      fieldId,
      set.id,
    )) as SavedRecSet & {
      cellGeometries?: Geom[] | null;
    };
    if (!fresh || typeof fresh.id !== "string") {
      return { set, cellGeometries: null };
    }
    const { cellGeometries, ...rest } = fresh;
    return {
      set: rest,
      cellGeometries: Array.isArray(cellGeometries) ? cellGeometries : null,
    };
  } catch (e) {
    if (e instanceof ApiError && e.code === "ERR_DEPENDENCY_UNAVAILABLE") {
      throw e;
    }
    if (e instanceof ApiError && e.status === 404 && localLegacyCopy(set)) {
      return { set, cellGeometries: null };
    }
    throw e;
  }
}

function localLegacyCopy(set: SavedRecSet): boolean {
  return !isFrozen(set) && set.soil != null;
}

async function currentBoundaryId(fieldId: string): Promise<string | undefined> {
  try {
    const current = await api.fetchFieldGrid(fieldId, { fields: "identity" });
    return current.boundary_version_id || undefined;
  } catch (e) {
    if (e instanceof ApiError && e.code === "ERR_DEPENDENCY_UNAVAILABLE") {
      throw e;
    }
    return undefined;
  }
}

async function gridGeoms(
  fieldId: string,
  gridId: string,
  cellCount: number | undefined,
): Promise<Geom[]> {
  const grid = await api.fetchFieldGrid(fieldId, { gridId });
  const count = cellCount ?? grid.cell_count;
  if (grid.boundary_version_id !== gridId || grid.geometries.length !== count) {
    throw new Error(CELLS_UNAVAILABLE);
  }
  return grid.geometries;
}

function snapshotGeoms(
  opened: OpenedRec,
  cellCount: number | undefined,
): Geom[] {
  const geoms = opened.cellGeometries;
  if (!geoms || (cellCount != null && geoms.length !== cellCount)) {
    throw new Error(CELLS_UNAVAILABLE);
  }
  return geoms;
}

async function savedGeoms(fieldId: string, opened: OpenedRec): Promise<Geom[]> {
  const cells = opened.set.frozen?.cells;
  if (cells?.kind === "grid" && cells.gridId) {
    return gridGeoms(fieldId, cells.gridId, cells.cellCount);
  }
  if (cells?.kind === "snapshot") return snapshotGeoms(opened, cells.cellCount);
  throw new Error(CELLS_UNAVAILABLE);
}

export async function frozenSpread(
  fieldId: string,
  opened: OpenedRec,
): Promise<FrozenSpread> {
  if (cellsUnavailable(opened.set)) throw new Error(CELLS_UNAVAILABLE);
  const currentId = await currentBoundaryId(fieldId);
  const savedId = savedBoundaryId(opened.set);
  return {
    geoms: await savedGeoms(fieldId, opened),
    notice: olderBoundaryNotice(savedId, currentId, opened.set.frozen?.at),
    fileNotice: fileClipNotice(savedId, currentId),
  };
}

export function frozenRateColumn(
  set: SavedRecSet,
  nutrient: FrozenNutrient,
  count: number,
): (number | null)[] | null {
  const column = set.frozen?.rates?.[nutrient];
  if (!Array.isArray(column) || column.length !== count) return null;
  return column.map((value) =>
    typeof value === "number" && Number.isFinite(value) ? value : null,
  );
}

function finiteRates(column: (number | null)[]): (number | null)[] {
  return column.map((value) =>
    typeof value === "number" && Number.isFinite(value) ? value : null,
  );
}

function storedColumn(
  set: SavedRecSet,
  nutrient: FrozenNutrient,
  count: number,
): (number | null)[] {
  if (!(set.nutrients ?? []).includes(nutrient)) {
    return Array.from({ length: count }, () => null);
  }
  const column = set.frozen?.rates?.[nutrient];
  if (!Array.isArray(column) || column.length !== count) {
    throw new Error(OPEN_FAILED);
  }
  return finiteRates(column);
}

function replaceRate(
  layer: ManifestLayer,
  geoms: Geom[],
  rates: (number | null)[],
): ManifestLayer {
  const previous =
    (
      layer.inline as {
        features?: {
          geometry?: unknown;
          properties?: Record<string, unknown>;
        }[];
      }
    )?.features ?? [];
  const byGeom = new Map<unknown, Record<string, unknown>>();
  for (const feature of previous) {
    if (feature.geometry != null && feature.properties) {
      byGeom.set(feature.geometry, feature.properties);
    }
  }
  const features: unknown[] = [];
  for (let i = 0; i < geoms.length; i++) {
    const value = rates[i];
    if (value == null || !Number.isFinite(value)) continue;
    const prior = byGeom.get(geoms[i]);
    features.push({
      type: "Feature",
      properties: prior ? { ...prior, value } : { value },
      geometry: geoms[i],
    });
  }
  const present = rates.filter(
    (value): value is number => value != null && Number.isFinite(value),
  );
  const max = present.length ? Math.max(...present) : 1;
  return {
    ...layer,
    inline: { type: "FeatureCollection", features } as never,
    value_min: 0,
    value_max: max > 0 ? max : 1,
    content_version: "frozen",
  };
}

function bareLayer(
  geoms: Geom[],
  rates: (number | null)[],
  nutrient: FrozenNutrient,
  visible: boolean,
): ManifestLayer {
  const title =
    nutrient === "lime"
      ? "Lime rec"
      : nutrient === "p2o5"
        ? "P₂O₅ rec"
        : "K₂O rec";
  const shell = {
    id: `fert-${nutrient}`,
    kind: "geojson",
    title,
    unit: nutrient === "lime" ? "t/ac" : "lbs/ac",
    default_visible: visible,
    default_opacity: 0.8,
    order: 60,
    content_version: "frozen",
    is_stale: false,
    colormap: "viridis",
    inline: { type: "FeatureCollection", features: [] },
  } as ManifestLayer;
  return replaceRate(shell, geoms, rates);
}

function paintFrozen(
  layers: ManifestLayer[],
  geoms: Geom[],
  key: FrozenNutrient,
  column: (number | null)[],
): ManifestLayer[] {
  const totalId = `fert-${key}-total`;
  const target = layers.some((layer) => layer.id === totalId)
    ? totalId
    : `fert-${key}`;
  return layers.map((layer) =>
    layer.id === target ? replaceRate(layer, geoms, column) : layer,
  );
}

function layersFromSoil(
  geoms: Geom[],
  set: SavedRecSet,
  soil: RecInputs,
  columns: Record<FrozenNutrient, (number | null)[]>,
): ManifestLayer[] {
  const built = assembleFertLayers(geoms, soil, {
    nutrients: set.nutrients ?? [],
    cation: !!set.cation,
    replacement: set.replacement ?? null,
    replP: set.replP ?? true,
    replK: set.replK ?? true,
    rateWindow: set.rateWindow,
    cationRatios:
      set.cationCaMg != null && set.cationMgK != null
        ? { caMg: set.cationCaMg, mgK: set.cationMgK }
        : undefined,
  });
  return FROZEN_NUTRIENTS.reduce(
    (layers, key) => paintFrozen(layers, geoms, key, columns[key]),
    built.layers,
  );
}

function columnsFor(
  set: SavedRecSet,
  count: number,
): Record<FrozenNutrient, (number | null)[]> {
  return {
    lime: storedColumn(set, "lime", count),
    p2o5: storedColumn(set, "p2o5", count),
    k2o: storedColumn(set, "k2o", count),
  };
}

export function frozenLayers(
  geoms: Geom[],
  set: SavedRecSet,
): { layers: ManifestLayer[]; summary: FertSummary } {
  const columns = columnsFor(set, geoms.length);
  const summary: FertSummary = {
    ...summarizeRecs(columns),
    cells: geoms.length,
  };
  const soil = set.soil as RecInputs | undefined;
  if (soil && Array.isArray(soil.ph)) {
    return { layers: layersFromSoil(geoms, set, soil, columns), summary };
  }
  const nutrients = (set.nutrients ?? []).filter(
    (nutrient): nutrient is FrozenNutrient =>
      nutrient === "lime" || nutrient === "p2o5" || nutrient === "k2o",
  );
  return {
    layers: nutrients.map((nutrient, index) =>
      bareLayer(geoms, columns[nutrient], nutrient, index === 0),
    ),
    summary,
  };
}
